import React from "react";

import { cn } from "../../utils/cn";
import { Heading } from "../ui/heading";

export interface PageHeaderProps {
    /** The page title. Rendered via `Heading` (h1). */
    title: React.ReactNode;
    /**
     * Optional content rendered below the title — typically the page's
     * KeyStats row (a Domain Component). Kept as a slot so this generic
     * header never imports the domain layer (dependency firewall).
     */
    children?: React.ReactNode;
    /** Extend / override the section chrome. */
    className?: string;
}

/**
 * PageHeader — the standard top-of-page header. A title over an optional
 * content slot (typically a KeyStats row), wrapped in the page-header
 * chrome: an edge-to-edge bottom divider and responsive horizontal
 * padding.
 *
 * Generic Component: it knows no Solomon/YaaS entity. The title is a
 * prop; domain content (KeyStats) is passed through the slot, so this
 * header never imports a Domain Component. Composes the existing
 * `Heading` primitive for the title rather than hand-rolling `Text`.
 *
 * @see PageHeader.intent.md   policy + recipes
 * @see PageHeader.layout.html intended layout
 */
export function PageHeader({
    title,
    children,
    className
}: PageHeaderProps): React.ReactElement {
    return (
        // Divider sits on the full-width <header> so it runs edge-to-edge;
        // padding lives on the inner wrapper (yaas-styles divider rule).
        <header className={cn("border-b border-line-base", className)}>
            <div className="px-4 md:px-6 py-6 flex flex-col gap-4">
                <Heading title={title} />
                {children}
            </div>
        </header>
    );
}
